import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import { useUserCulture } from '@app/hooks'
import {
  Listing,
  ListingLocation,
  Project,
  ProjectLocation,
  ProjectPriceValue,
} from '@app/models'
import {
  getImageUrl,
  getListingPriceFormatted,
  getUserCurrency,
} from '@app/utils'

import { getOriginalData } from '@lib/utils'

import {
  CARD_SEPARATOR,
  SUBTITLE_TRANSLATION_CONFIG,
  isSubtitleLabelKey,
} from './constants'
import {
  AddressLike,
  BuildProjectSubtitleEntity,
  ListingCardData,
  SubtitleLabelKey,
  UseListingCardResult,
} from './types'

const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const sanitizeStreetLine = (rawLine: string, city?: string | null): string => {
  if (!rawLine) {
    return ''
  }

  const cityPattern = city
    ? new RegExp('\\b' + escapeRegExp(city) + '\\b', 'ig')
    : null

  const cleanedFragments = rawLine
    .split(',')
    .map((fragment) => fragment.trim())
    .map((fragment) => {
      let cleaned = fragment

      if (cityPattern) {
        cleaned = cleaned.replace(cityPattern, '').trim()
      }

      return cleaned
        .replace(/\s{2,}/g, ' ')
        .replace(/^[,\s]+|[,\s]+$/g, '')
        .trim()
    })
    .filter(Boolean)

  const uniqueFragments = [...new Set(cleanedFragments)]

  return uniqueFragments.join(', ')
}

const composeAddressLine = (location?: AddressLike): string => {
  if (!location) {
    return ''
  }

  const segments: string[] = []
  const citySegments = [location.city, location.postcode]
    .map((value) => (value ? String(value).trim() : ''))
    .filter(Boolean)

  if (citySegments.length) {
    segments.push(citySegments.join(', '))
  }

  const streetLine = [location.address1, location.address2]
    .map((line) => (line ? sanitizeStreetLine(line, location.city) : ''))
    .find((line) => Boolean(line))

  if (streetLine) {
    segments.push(streetLine)
  }

  return segments.join(', ')
}

export const useListingCard = (): UseListingCardResult => {
  const { t, i18n } = useTranslation()
  const culture = useUserCulture()

  const translateLabel = useCallback(
    (labelKey: SubtitleLabelKey, count?: number) => {
      const config = SUBTITLE_TRANSLATION_CONFIG[labelKey]
      const useCount = config.supportsPlural && typeof count === 'number'
      const value = useCount ? t(config.key, { count }) : t(config.key)
      if (typeof value !== 'string') {
        return ''
      }
      return value.toLowerCase()
    },
    [t, i18n.language]
  )

  const getRangeString = useCallback(
    (x?: number, y?: number, shortMode = false) => {
      const fromLabel = translateLabel('from') || 'from'
      const toLabel = translateLabel('to') || 'to'
      const hasX = typeof x === 'number' && !Number.isNaN(x) && x !== 0
      const hasY = typeof y === 'number' && !Number.isNaN(y) && y !== 0

      if (hasX && hasY && x !== y) {
        if (shortMode) {
          return `${x}-${y}`
        }
        return `${fromLabel} ${x} ${toLabel} ${y}`
      }

      if (hasX && hasY && x === y) {
        return `${x}`
      }

      if (!hasX && hasY) {
        return `${toLabel} ${y}`
      }

      if (hasX && !hasY) {
        return `${fromLabel} ${x}`
      }

      return ''
    },
    [translateLabel]
  )

  const buildProjectSubtitle = useCallback(
    (entity: BuildProjectSubtitleEntity) => {
      if (!entity) {
        return ''
      }

      const subtitleData: string[] = []
      const { numberOf, area } = entity

      const effectiveUnits = entity.units ?? numberOf?.units
      if (effectiveUnits && effectiveUnits > 0) {
        const unitsLabel = translateLabel('unit', effectiveUnits)
        subtitleData.push(
          unitsLabel ? `${effectiveUnits} ${unitsLabel}` : `${effectiveUnits}`
        )
      }

      const pickCount = (min?: number, max?: number) => {
        if (typeof max === 'number' && !Number.isNaN(max) && max > 0) {
          return max
        }
        if (typeof min === 'number' && !Number.isNaN(min) && min > 0) {
          return min
        }
        return undefined
      }

      const areaUnitId = area?.unit?.id
      let unitTranslation = ''
      if (areaUnitId && isSubtitleLabelKey(areaUnitId)) {
        unitTranslation = translateLabel(areaUnitId, area?.max ?? area?.min)
      } else if (area?.unit?.name) {
        unitTranslation = area.unit.name.toLowerCase()
      } else {
        unitTranslation = translateLabel('SquareMeter', area?.max ?? area?.min)
      }

      const roomsRange = getRangeString(
        numberOf?.roomsMin,
        numberOf?.roomsMax,
        true
      )
      if (roomsRange) {
        const roomsLabel = translateLabel(
          'room',
          pickCount(numberOf?.roomsMin, numberOf?.roomsMax)
        )
        subtitleData.push(
          roomsLabel ? `${roomsRange} ${roomsLabel}` : `${roomsRange}`
        )
      }

      const bedroomsRange = getRangeString(
        numberOf?.bedroomsMin,
        numberOf?.bedroomsMax,
        true
      )
      if (bedroomsRange) {
        const bedLabel = translateLabel(
          'bed',
          pickCount(numberOf?.bedroomsMin, numberOf?.bedroomsMax)
        )
        subtitleData.push(
          bedLabel ? `${bedroomsRange} ${bedLabel}` : `${bedroomsRange}`
        )
      }

      const areaRange = getRangeString(area?.min, area?.max, true)
      if (areaRange) {
        subtitleData.push(
          unitTranslation ? `${areaRange} ${unitTranslation}` : `${areaRange}`
        )
      }

      return subtitleData.join(CARD_SEPARATOR)
    },
    [getRangeString, translateLabel]
  )

  const buildListingSubtitle = useCallback(
    (listing: Listing) => {
      if (!listing) {
        return ''
      }

      if (listing.class === 'ProjectListing') {
        return buildProjectSubtitle({
          numberOf: listing.numberOf,
          area: listing.area,
          units: listing.units,
        })
      }

      const subtitleData: string[] = []

      if (listing?.type?.name) {
        subtitleData.push(listing.type.name)
      }

      const numberOf = listing.numberOf

      if (numberOf?.rooms) {
        const roomsLabel = translateLabel('room', numberOf.rooms)
        subtitleData.push(
          roomsLabel ? `${numberOf.rooms} ${roomsLabel}` : `${numberOf.rooms}`
        )
      }

      if (numberOf?.bedrooms) {
        const bedLabel = translateLabel('bed', numberOf.bedrooms)
        subtitleData.push(
          bedLabel ? `${numberOf.bedrooms} ${bedLabel}` : `${numberOf.bedrooms}`
        )
      }

      if (numberOf?.bathrooms) {
        const bathLabel = translateLabel('bath', numberOf.bathrooms)
        subtitleData.push(
          bathLabel
            ? `${numberOf.bathrooms} ${bathLabel}`
            : `${numberOf.bathrooms}`
        )
      }

      if (listing.area?.living) {
        const areaUnitId = listing.area.unit?.id
        let unitTranslation = ''
        if (areaUnitId && isSubtitleLabelKey(areaUnitId)) {
          unitTranslation = translateLabel(areaUnitId, listing.area.living)
        } else if (listing.area.unit?.name) {
          unitTranslation = listing.area.unit.name.toLowerCase()
        } else {
          unitTranslation = translateLabel('SquareMeter', listing.area.living)
        }
        const unitSuffix = unitTranslation ? ` ${unitTranslation}` : ''
        subtitleData.push(`${listing.area.living}${unitSuffix}`)
      }

      const floorName = (listing as Listing & { floor?: { name?: string } })
        .floor?.name
      if (floorName) {
        const floorLabel = translateLabel('floor') || 'floor'
        const isNumericFloor = !Number.isNaN(Number(floorName))
        subtitleData.push(
          isNumericFloor ? `${floorName} ${floorLabel}` : `${floorName}`
        )
      }

      return subtitleData.join(CARD_SEPARATOR)
    },
    [buildProjectSubtitle, translateLabel]
  )

  const getListingTitle = useCallback((listing: Listing) => {
    return (
      getOriginalData(listing?.title || listing?.name) ||
      listing?.automaticTitle ||
      ''
    )
  }, [])

  const getListingAddressLine = useCallback(
    (location: ListingLocation | undefined) => composeAddressLine(location),
    []
  )

  const getListingImage = useCallback((listing: Listing) => {
    return listing?.resources?.pictures?.items[0]?.url || getImageUrl()
  }, [])

  const getListingPrice = useCallback(
    (listing: Listing) => {
      if (listing?.price?.display) {
        return listing.price.display
      }

      const automaticDisplay = getOriginalData(listing?.price?.automaticDisplay)
      if (automaticDisplay) {
        return automaticDisplay
      }

      const values = listing?.price?.values
      if (values && values.length) {
        const userCurrency = getUserCurrency()
        const preferredValue =
          values.find((value) => value.currencyId === userCurrency) || values[0]

        if (preferredValue && preferredValue.value !== undefined) {
          return getListingPriceFormatted({
            currencyId: preferredValue.currencyId,
            amount: preferredValue.value,
            currentLocale: culture,
            signDisplay: 'never',
          })
        }
      }

      const isPriceOnDemand =
        listing?.price?.values &&
        listing.price.values.length > 0 &&
        listing.price.values[0].value === undefined

      if (isPriceOnDemand) {
        return t('Chat.PriceOnDemand')
      }

      return ''
    },
    [culture, t]
  )

  const getProjectTitle = useCallback((project: Project) => {
    return (
      getOriginalData(project?.title || project?.name) ||
      getOriginalData(project?.automaticTitles) ||
      getOriginalData(project?.automaticNames) ||
      ''
    )
  }, [])

  const getProjectAddressLine = useCallback(
    (location: ProjectLocation | undefined) => composeAddressLine(location),
    []
  )

  const getProjectImage = useCallback((project: Project) => {
    return project?.resources?.pictures?.items[0]?.url || getImageUrl()
  }, [])

  const getDisplayFromValues = useCallback(
    (values?: ProjectPriceValue[]) => {
      if (!values || !values.length) {
        return ''
      }

      const entry = values.find((value) => value.min || value.max) || values[0]
      if (!entry) {
        return ''
      }

      const formatAmount = (amount: number | undefined) => {
        if (amount === undefined || amount === null) {
          return ''
        }
        return getListingPriceFormatted({
          currencyId: entry.currencyId,
          amount,
          currentLocale: culture,
          signDisplay: 'never',
        })
      }

      const hasMin = entry.min !== undefined && entry.min !== null
      const hasMax = entry.max !== undefined && entry.max !== null

      if (hasMin && hasMax && entry.min !== entry.max) {
        const minFormatted = formatAmount(entry.min)
        const maxFormatted = formatAmount(entry.max)
        if (minFormatted && maxFormatted) {
          return `${minFormatted} - ${maxFormatted}`
        }
      }

      if (hasMin) {
        return formatAmount(entry.min)
      }

      if (hasMax) {
        return formatAmount(entry.max)
      }

      return ''
    },
    [culture]
  )

  const getProjectPrice = useCallback(
    (project: Project) => {
      const automaticDisplay = getOriginalData(project?.price?.automaticDisplay)
      if (automaticDisplay) {
        return automaticDisplay
      }

      const sale = getDisplayFromValues(project.price?.sale?.value)
      if (sale) {
        return sale
      }

      const rent = getDisplayFromValues(project.price?.rent?.value)
      if (rent) {
        return rent
      }

      return t('Chat.PriceOnDemand')
    },
    [getDisplayFromValues, t]
  )

  const getListingCardData = useCallback(
    (listing: Listing): ListingCardData => {
      const imageUrl = getListingImage(listing)
      const imageAlt = getListingTitle(listing)
      const addressLine = getListingAddressLine(listing.location)
      const title = addressLine || imageAlt
      const subtitle = buildListingSubtitle(listing)
      const price = getListingPrice(listing)

      return {
        imageUrl,
        imageAlt,
        title,
        subtitle: subtitle || undefined,
        price,
      }
    },
    [
      buildListingSubtitle,
      getListingAddressLine,
      getListingImage,
      getListingPrice,
      getListingTitle,
    ]
  )

  const getProjectCardData = useCallback(
    (project: Project): ListingCardData => {
      const imageUrl = getProjectImage(project)
      const imageAlt = getProjectTitle(project)
      const addressLine = getProjectAddressLine(project.location)
      const title = addressLine || imageAlt
      const price = getProjectPrice(project)
      const subtitle = buildProjectSubtitle({
        numberOf: project.numberOf,
        area: project.area,
        units: project.numberOf?.units,
      })

      return {
        imageUrl,
        imageAlt,
        title,
        subtitle: subtitle || undefined,
        price,
      }
    },
    [
      buildProjectSubtitle,
      getProjectAddressLine,
      getProjectImage,
      getProjectPrice,
      getProjectTitle,
    ]
  )

  return {
    getListingCardData,
    getProjectCardData,
  }
}

export type { UseListingCardResult }
